// src/components/CotationAssistPanel.jsx
// Aide à la cotation (team/admin) : recherche un artiste dans le référentiel Artprice
// (RPC search_artists), affiche sa côte + des ancrages calculés, et pré-remplit la fiche
// de cotation existante. Repère de marché au niveau ARTISTE — pas la valeur d'une œuvre.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import ArtistAutocomplete from "./artwork/ArtistAutocomplete";

const fmtUsd = (n) => {
  const a = Number(n);
  if (n == null || !Number.isFinite(a)) return "—";
  if (a >= 1e9) return "$" + (a / 1e9).toFixed(2).replace(/\.?0+$/, "") + " Md";
  if (a >= 1e6) return "$" + (a / 1e6).toFixed(2).replace(/\.?0+$/, "") + " M";
  if (a >= 1e3) return "$" + Math.round(a / 1e3) + " k";
  return "$" + Math.round(a);
};
const pct = (r) => (r == null ? "—" : Math.round(Number(r) * 100) + " %");

const GOLD = "#a9843f";
const BORDER = "#e5e0d8";

export default function CotationAssistPanel({ onPrefill }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [artist, setArtist] = useState(null);
  const [quote, setQuote] = useState(undefined); // undefined=vide/chargement, null=aucune côte

  const onSelect = async (cand) => {
    setArtist(cand);
    setQuery(cand.name || "");
    setQuote(undefined);
    try {
      const { data } = await supabase
        .from("artist_quotations")
        .select("rank,priority_tier,turnover_usd,sold_lots,unsold_lots,lots_offered,sell_through_rate,best_result_usd,new_personal_record,source_year")
        .eq("artist_id", cand.id)
        .order("source_year", { ascending: false })
        .limit(1);
      setQuote((data && data[0]) || null);
    } catch {
      setQuote(null);
    }
  };

  const avgSold =
    quote && quote.turnover_usd && quote.sold_lots
      ? Math.round(Number(quote.turnover_usd) / Number(quote.sold_lots))
      : null;

  const datesOf = (a) =>
    a && a.birth_year ? (a.death_year ? `${a.birth_year}–${a.death_year}` : `${a.birth_year}–`) : "";

  const doPrefill = () => {
    if (!artist) return;
    const note = quote
      ? t("cotationAssist.note", {
          defaultValue:
            "Repères Artprice {{year}} — rang #{{rank}}, meilleur résultat {{best}}, prix moyen adjugé {{avg}} ({{sold}} lots vendus), taux de vente {{str}}. Montants en USD.",
          year: quote.source_year,
          rank: quote.rank,
          best: fmtUsd(quote.best_result_usd),
          avg: fmtUsd(avgSold),
          sold: quote.sold_lots,
          str: pct(quote.sell_through_rate),
        })
      : "";
    onPrefill?.({
      artist: artist.name,
      average_value: avgSold != null ? String(avgSold) : "",
      notes: note,
    });
  };

  const Metric = ({ label, value, strong }) => (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px", background: "#fff" }}>
      <div style={{ fontSize: 11, letterSpacing: ".04em", textTransform: "uppercase", color: "#8a8378" }}>{label}</div>
      <div style={{ fontSize: strong ? 20 : 16, fontWeight: strong ? 700 : 600, color: "#1a1a1a", fontFamily: "Georgia, serif" }}>{value}</div>
    </div>
  );

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 14, padding: 20, background: "#faf6ec", marginBottom: 28 }}>
      <div style={{ fontSize: 11, letterSpacing: ".18em", textTransform: "uppercase", color: GOLD, marginBottom: 6 }}>
        {t("cotationAssist.eyebrow", { defaultValue: "Aide à la cotation" })}
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#1a1a1a", margin: "0 0 4px", fontFamily: "Georgia, serif" }}>
        {t("cotationAssist.title", { defaultValue: "Côte de l'artiste (référentiel Artprice)" })}
      </h2>
      <p style={{ fontSize: 13, color: "#6b6459", margin: "0 0 14px" }}>
        {t("cotationAssist.subtitle", { defaultValue: "Cherchez un artiste pour afficher sa côte et des repères de marché, puis pré-remplir la fiche." })}
      </p>

      <ArtistAutocomplete
        name="cotation_artist"
        id="cotation_artist"
        value={query}
        onChange={(e) => { setQuery(e.target.value); if (!e.target.value) { setArtist(null); setQuote(undefined); } }}
        onSelectArtist={onSelect}
        placeholder={t("cotationAssist.searchPlaceholder", { defaultValue: "Nom de l'artiste…" })}
        className="w-full border rounded-md py-2 px-3"
      />

      {artist && quote === undefined && (
        <p style={{ fontSize: 13, color: "#8a8378", marginTop: 12 }}>{t("cotationAssist.loading", { defaultValue: "Chargement de la côte…" })}</p>
      )}

      {artist && quote === null && (
        <p style={{ fontSize: 13, color: "#8a8378", marginTop: 12 }}>
          {t("cotationAssist.noQuote", { defaultValue: "Aucune côte de référence pour cet artiste (hors Top 500 Artprice). Saisie manuelle possible ci-dessous." })}
        </p>
      )}

      {artist && quote && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <span style={{ fontSize: 17, fontWeight: 700, fontFamily: "Georgia, serif", color: "#1a1a1a" }}>{artist.name}</span>
            {datesOf(artist) && <span style={{ fontSize: 13, color: "#8a8378" }}>{datesOf(artist)}</span>}
            <span style={{ fontSize: 12, fontWeight: 600, color: GOLD, background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 999, padding: "2px 10px" }}>
              Artprice #{quote.rank} · {quote.priority_tier} · {quote.source_year}
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            <Metric strong label={t("cotationAssist.avgSold", { defaultValue: "Prix moyen adjugé" })} value={fmtUsd(avgSold)} />
            <Metric strong label={t("cotationAssist.best", { defaultValue: "Meilleur résultat" })} value={fmtUsd(quote.best_result_usd)} />
            <Metric label={t("cotationAssist.turnover", { defaultValue: "Chiffre d'affaires" })} value={fmtUsd(quote.turnover_usd)} />
            <Metric label={t("cotationAssist.sellThrough", { defaultValue: "Taux de vente" })} value={pct(quote.sell_through_rate)} />
            <Metric label={t("cotationAssist.sold", { defaultValue: "Lots vendus / présentés" })} value={`${quote.sold_lots ?? "—"} / ${quote.lots_offered ?? "—"}`} />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={doPrefill}
              style={{ background: "#1a1a1a", color: "#f9f7f4", border: "none", borderRadius: 9, padding: "10px 18px", fontWeight: 600, cursor: "pointer" }}
            >
              {t("cotationAssist.prefill", { defaultValue: "Pré-remplir la fiche de cotation" })}
            </button>
            <span style={{ fontSize: 12, color: "#8a8378" }}>
              {t("cotationAssist.disclaimer", { defaultValue: "Repère au niveau artiste (USD) — à ajuster selon l'œuvre. Ne remplace pas l'expertise." })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
